/** Helpers for Event Schedule step (multi-day + multi-time). */

export function emptyScheduleDay() {
  return {
    active: true,
    date: "", // YYYY-MM-DD
    times: [""], // HH:mm
    recurring: false,
  };
}

export function formatDayLabel(dateStr) {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function formatTimeLabel(timeStr) {
  if (!timeStr) return "";
  const [h, m] = String(timeStr).split(":").map(Number);
  if (Number.isNaN(h)) return timeStr;
  const d = new Date();
  d.setHours(h, m || 0, 0, 0);
  return d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

export function scheduleSummaryLines(days = []) {
  const lines = [];
  for (const day of days || []) {
    if (!day?.active || !day.date) continue;
    for (const t of day.times || []) {
      if (!t) continue;
      lines.push(`${formatDayLabel(day.date)} ${formatTimeLabel(t)}`);
    }
  }
  return lines;
}

export function formatShowSlotLabel(dateStr, timeStr) {
  if (!dateStr || !timeStr) return "";
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return `${dateStr} | ${timeStr}`;
  const weekday = d.toLocaleDateString("en-GB", { weekday: "short" });
  return `${weekday}, ${formatDayLabel(dateStr)} | ${formatTimeLabel(timeStr)}`;
}

/** Unique calendar dates from schedule (for date-wise tickets). */
export function listScheduleDates(days = []) {
  const dates = [];
  const seen = new Set();
  for (const day of days || []) {
    if (!day?.active || !day.date) continue;
    if (seen.has(day.date)) continue;
    seen.add(day.date);
    const d = new Date(`${day.date}T12:00:00`);
    const weekday = Number.isNaN(d.getTime())
      ? ""
      : d.toLocaleDateString("en-GB", { weekday: "short" });
    dates.push({
      date: day.date,
      label: formatDayLabel(day.date),
      shortLabel: weekday ? `${weekday}, ${formatDayLabel(day.date)}` : formatDayLabel(day.date),
    });
  }
  return dates;
}

/**
 * Make ticket type unique across dates so inventory matching (by type) stays correct.
 * UI can still show the short name; storage uses "GOLD · 31 Jul 2026".
 */
export function uniqueTicketType(name, date) {
  const base = String(name || "").trim();
  if (!base) return "";
  if (!date) return base;
  const label = formatDayLabel(date);
  if (!label) return base;
  // Already suffixed for this date
  if (base.endsWith(` · ${label}`) || base.endsWith(` (${label})`)) return base;
  return `${base} · ${label}`;
}

/** Display name without date suffix */
export function ticketDisplayName(type = "") {
  return String(type || "")
    .replace(/\s*[·•]\s*\d{1,2}\s+\w{3}\s+\d{4}\s*$/i, "")
    .replace(/\s*\(\d{1,2}\s+\w{3}\s+\d{4}\)\s*$/i, "")
    .trim() || String(type || "");
}

/**
 * Recover YYYY-MM-DD from storage type like "Gold · 26 Aug 2026".
 * Used when date field was cleared incorrectly.
 */
export function ticketDateFromType(type = "") {
  const m = String(type || "").match(
    /[·•]\s*(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})\s*$/
  );
  if (!m) return "";
  const day = Number(m[1]);
  const monMap = {
    jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
    jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
  };
  const mi = monMap[String(m[2]).toLowerCase()];
  const year = Number(m[3]);
  if (!Number.isFinite(day) || mi == null || !Number.isFinite(year)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${year}-${pad(mi + 1)}-${pad(day)}`;
}

/** Flatten schedule days into show slots for tickets UI. */
export function listShowSlots(days = []) {
  const slots = [];
  for (const day of days || []) {
    if (!day?.active || !day.date) continue;
    for (const t of day.times || []) {
      if (!t) continue;
      slots.push({
        date: day.date,
        time: t,
        label: formatShowSlotLabel(day.date, t),
      });
    }
  }
  return slots;
}

/** Combine YYYY-MM-DD + HH:mm → datetime-local string */
export function combineDateTime(dateStr, timeStr) {
  if (!dateStr || !timeStr) return "";
  return `${dateStr}T${timeStr}`;
}

/**
 * Sync scheduleDays into legacy date / endDate / showtimes fields.
 * Stores face time as UTC wall-clock (18:30 → …T18:30:00.000Z), matching backend.
 */
export function syncScheduleToLegacy(days = [], durationMinutes = 120) {
  const slots = [];
  const durMs = Math.max(Number(durationMinutes) || 120, 30) * 60 * 1000;

  for (const day of days) {
    if (!day?.active || !day.date) continue;
    for (const t of day.times || []) {
      if (!t) continue;
      const m = String(day.date).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) continue;
      const [hh, mm] = String(t).split(":").map(Number);
      if (Number.isNaN(hh)) continue;
      const start = new Date(
        Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), hh, mm || 0, 0, 0)
      );
      if (Number.isNaN(start.getTime())) continue;
      slots.push({ start, end: new Date(start.getTime() + durMs) });
    }
  }

  slots.sort((a, b) => a.start - b.start);

  if (!slots.length) {
    return {
      date: "",
      eventEndDate: "",
      showtimes: [{ startTime: "", endTime: "", availableSeats: "", ticketPrice: "" }],
    };
  }

  const toWall = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
  };

  const first = slots[0];
  const last = slots[slots.length - 1];

  return {
    date: toWall(first.start),
    eventEndDate: toWall(last.end),
    showtimes: slots.map((s) => ({
      startTime: toWall(s.start),
      endTime: toWall(s.end),
      availableSeats: "",
      ticketPrice: "",
    })),
  };
}

export function isScheduleComplete(days = [], minDate) {
  const active = (days || []).filter((d) => d.active);
  if (!active.length) return false;
  return active.some((d) => {
    if (!d.date) return false;
    if (minDate && d.date < minDate) return false;
    return (d.times || []).some((t) => Boolean(t));
  });
}

export function minDateISO(fromDate = new Date()) {
  const d = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate() + 1);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
